import { showUserAlert } from "../utils/userErrorMessage";
// src/pages/SafetyEdit.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../Component/Header";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { supabase } from "../supabase/client";

const ISSUE_OPTIONS = ["민원", "사고", "과태료"];

function isoToLocalDatetime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const MM = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mm = pad(d.getMinutes());
  return `${yyyy}-${MM}-${dd}T${hh}:${mm}`;
}
function localToISO(dtLocal) {
  if (!dtLocal) return null;
  const d = new Date(dtLocal);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function SafetyEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const company = localStorage.getItem("email") || "";

  const [saving, setSaving] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [carOptions, setCarOptions] = useState([]);

  const [form, setForm] = useState({
    issue: "",
    driver_name: "",
    car_number: "",
    dateLocal: "",
    place: "",
    opponent: "",
    summary: "",
    insurance: "",
    process: "",
    etc: "",
    surcharge: "",
  });

  const isAccidentLike = useMemo(
    () => form.issue === "사고" || form.issue === "과태료",
    [form.issue]
  );

  // load drivers
  useEffect(() => {
    const loadDrivers = async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("driver_name")
        .eq("company", company)
        .eq("role", "driver");
      if (!error) {
        const list = (data || [])
          .map((r) => r?.driver_name)
          .filter(Boolean)
          .filter((v, i, arr) => arr.indexOf(v) === i);
        setDrivers(list);
      }
    };
    if (company) loadDrivers();
  }, [company]);

  // load record
  useEffect(() => {
    const load = async () => {
      const { data, error } = await supabase
        .from("safety")
        .select("*")
        .eq("id", id)
        .eq("company", company)
        .maybeSingle();
      if (error) {
        showUserAlert("데이터 로드 실패: " + error.message);
        return;
      }
      if (!data) {
        showUserAlert("데이터가 없습니다.");
        navigate("/safety");
        return;
      }
      setForm({
        issue: data.issue || "",
        driver_name: data.driver_name || "",
        car_number: data.car_number || "",
        dateLocal: isoToLocalDatetime(data.date),
        place: data.place || "",
        opponent: data.opponent || "",
        summary: data.summary || "",
        insurance: data.insurance || "",
        process: data.process || "",
        etc: data.etc || "",
        surcharge: data.surcharge ?? "",
      });
    };
    if (id) load();
  }, [company, id, navigate]);

  // driver → car options
  useEffect(() => {
    const loadCars = async () => {
      setCarOptions([]);
      if (!form.driver_name) return;
      const { data, error } = await supabase
        .from("cars")
        .select("car_number, car_number_only, driver")
        .eq("company", company)
        .eq("driver", form.driver_name);
      if (!error) {
        const list = (data || []).map((c) => ({
          car_number: c.car_number || c.car_number_only || "",
        })).filter(c => !!c.car_number);
        setCarOptions(list);
      }
    };
    if (company) loadCars();
  }, [form.driver_name, company]);

  const canSave =
    company && form.issue && form.driver_name && form.dateLocal && form.summary.trim();

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const payload = {
        company,
        issue: form.issue,
        driver_name: form.driver_name,
        car_number: form.car_number || null,
        date: localToISO(form.dateLocal),
        place: form.place || null,
        opponent: form.issue === "민원" ? null : (form.opponent || null),
        summary: form.summary || null,
        insurance: isAccidentLike ? (form.insurance || null) : null,
        process: isAccidentLike ? (form.process || null) : null,
        etc: isAccidentLike ? (form.etc || null) : null,
        surcharge: isAccidentLike ? (form.surcharge || null) : null,
      };
      const { error } = await supabase
        .from("safety")
        .update(payload)
        .eq("id", id)
        .eq("company", company);
      if (error) throw error;
      showUserAlert("수정되었습니다.");
      navigate("/safety");
    } catch (e) {
      showUserAlert("저장 실패: " + (e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("정말 삭제할까요?")) return;
    const { error } = await supabase
      .from("safety")
      .delete()
      .eq("id", id)
      .eq("company", company);
    if (error) return showUserAlert("삭제 실패: " + error.message);
    showUserAlert("삭제되었습니다.");
    navigate("/safety");
  };

  return (
    <div className="space-y-4">
      <Header />

      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">안전 보고서 수정</h2>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(-1)}>뒤로</Button>
            <Button variant="destructive" onClick={handleDelete}>삭제</Button>
            <Button onClick={handleSave} disabled={!canSave || saving}>
              {saving ? "저장중..." : "저장"}
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="bg-white space-y-4">
            {/* 형태 & 운전자 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-1">형태(issue)</label>
                <select
                  className="border rounded px-3 py-2 w-full"
                  value={form.issue}
                  onChange={(e) => setForm((p) => ({ ...p, issue: e.target.value }))}
                >
                  <option value="">선택</option>
                  {ISSUE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">운전자(driver_name)</label>
                <select
                  className="border rounded px-3 py-2 w-full"
                  value={form.driver_name}
                  onChange={(e) => setForm((p) => ({ ...p, driver_name: e.target.value }))}
                >
                  <option value="">선택</option>
                  {drivers.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* 차량 & 일시 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-1">자기 차량(car_number)</label>
                {carOptions.length <= 1 ? (
                  <Input
                    value={form.car_number}
                    onChange={(e) => setForm((p) => ({ ...p, car_number: e.target.value }))}
                    placeholder="자동 채움/직접 입력"
                  />
                ) : (
                  <select
                    className="border rounded px-3 py-2 w-full"
                    value={form.car_number}
                    onChange={(e) => setForm((p) => ({ ...p, car_number: e.target.value }))}
                  >
                    <option value="">선택</option>
                    {carOptions.map((c) => (
                      <option key={c.car_number} value={c.car_number}>{c.car_number}</option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">일시(date, timestamptz)</label>
                <input
                  type="datetime-local"
                  className="border rounded px-3 py-2 w-full"
                  value={form.dateLocal}
                  onChange={(e) => setForm((p) => ({ ...p, dateLocal: e.target.value }))}
                />
              </div>
            </div>

            {/* 장소 / 상대차량(민원 제외) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-1">장소(place)</label>
                <Input
                  value={form.place}
                  onChange={(e) => setForm((p) => ({ ...p, place: e.target.value }))}
                  placeholder="예: 서울역 앞"
                />
              </div>
              {form.issue !== "민원" && (
                <div>
                  <label className="block text-sm font-semibold mb-1">상대 차량(opponent)</label>
                  <Input
                    value={form.opponent}
                    onChange={(e) => setForm((p) => ({ ...p, opponent: e.target.value }))}
                    placeholder="예: 12가3456 / 버스 / 택시 등"
                  />
                </div>
              )}
            </div>

            {/* 개요 */}
            <div>
              <label className="block text-sm font-semibold mb-1">개요(summary)</label>
              <textarea
                className="w-full border rounded px-3 py-2 h-40"
                value={form.summary}
                onChange={(e) => setForm((p) => ({ ...p, summary: e.target.value }))}
                placeholder="사건 개요를 자세히 입력하세요."
              />
            </div>

            {/* 사고/과태료 전용 */}
            {isAccidentLike && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">보험(insurance)</label>
                  <Input
                    value={form.insurance}
                    onChange={(e) => setForm((p) => ({ ...p, insurance: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">처리(process)</label>
                  <Input
                    value={form.process}
                    onChange={(e) => setForm((p) => ({ ...p, process: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">비고(etc)</label>
                  <Input
                    value={form.etc}
                    onChange={(e) => setForm((p) => ({ ...p, etc: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">지급금액(surcharge)</label>
                  <Input
                    value={form.surcharge}
                    onChange={(e) => setForm((p) => ({ ...p, surcharge: e.target.value }))}
                    placeholder="예: 200000"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
